/**
 * Privacy Policy Component - Collapsible Section
 * All content is in DOM for SEO/AdSense but starts collapsed for clean UI
 */

import { useState, useEffect } from 'react'
import { Shield, Lock, Eye, Cookie, Mail, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react'
import '../privacyPolicy.css'

const PrivacyPolicy = () => {
  const [isExpanded, setIsExpanded] = useState(false)

  // Auto-expand if URL hash matches or if scrolled to from footer
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#privacy') {
        setIsExpanded(true)
      }
    }

    // Check on mount
    handleHashChange()

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange)
    
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
    
    // Scroll to section when expanding
    if (!isExpanded) {
      setTimeout(() => {
        const element = document.getElementById('privacy')
        if (element) {
          const offset = 80
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - offset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }
      }, 100)
    }
  }

  return (
    <section className="privacy-policy-section" id="privacy">
      <div className="privacy-container">
        
        {/* Collapsible Header - Always Visible */}
        <div className="privacy-header-collapsible" onClick={toggleExpanded}>
          <div className="privacy-header-content">
            <Shield size={32} color="#ff6b35" />
            <div className="header-text">
              <h2>Privacy Policy</h2>
              <p className="last-updated">Last Updated: December 14, 2024</p>
            </div>
          </div>
          <button 
            className="toggle-button"
            aria-label={isExpanded ? 'Collapse privacy policy' : 'Expand privacy policy'}
          >
            {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </button>
        </div>

        {/* Quick Summary - Always Visible */}
        <div className="privacy-summary-preview">
          <p className="summary-preview-text">
            <strong>Your financial data stays private.</strong> All calculations happen in your browser. 
            We use Google Analytics and AdSense for analytics and advertising. Click to read full policy.
          </p>
        </div>

        {/* Expandable Content */}
        <div className={`privacy-content-expandable ${isExpanded ? 'expanded' : 'collapsed'}`}>
          
          {/* Full Header (when expanded) */}
          <div className="privacy-header-full">
            <p className="intro-text">
              Your privacy matters to us. This policy explains what information is collected 
              when you use Zero Debt Journey, how it's used, and your rights regarding your data.
            </p>
          </div>

          {/* Quick Summary Box */}
          <div className="privacy-summary">
            <h3>
              <Eye size={24} color="#ff6b35" />
              Privacy at a Glance
            </h3>
            <div className="summary-grid">
              <div className="summary-item">
                <Lock size={20} color="#10b981" />
                <div>
                  <h4>Your Financial Data Stays Private</h4>
                  <p>
                    All calculations happen in your browser. We never see, store, or 
                    transmit your debt amounts, interest rates, or payment information.
                  </p>
                </div>
              </div>
              <div className="summary-item">
                <Cookie size={20} color="#3b82f6" />
                <div>
                  <h4>Third-Party Analytics Only</h4>
                  <p>
                    We use Google Analytics and AdSense, which collect anonymous usage 
                    data to improve our service and show relevant ads.
                  </p>
                </div>
              </div>
              <div className="summary-item">
                <Shield size={20} color="#ff6b35" />
                <div>
                  <h4>No Account Required</h4>
                  <p>
                    You don't create an account or provide personal information. 
                    You can use our calculator completely anonymously.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="privacy-content">
            
            {/* Section 1: What We Don't Collect */}
            <article className="privacy-section">
              <h3>1. What We DON'T Collect</h3>
              <p>
                Let's start with what we <strong>don't</strong> collect or have access to:
              </p>
              <ul className="privacy-list">
                <li>
                  <strong>Your Financial Information:</strong> All debt calculations happen 
                  entirely in your browser using JavaScript. Your credit card balance, interest 
                  rate, and payment amounts never leave your device.
                </li>
                <li>
                  <strong>Personal Identification:</strong> We don't collect your name, email 
                  address, phone number, or any other personally identifiable information.
                </li>
                <li>
                  <strong>Account Data:</strong> We don't require account creation, passwords, 
                  or login credentials.
                </li>
                <li>
                  <strong>Payment Information:</strong> We don't process payments or collect 
                  credit card information (this is a free tool).
                </li>
                <li>
                  <strong>Precise Location:</strong> We don't track your exact location using GPS.
                </li>
              </ul>
              <div className="info-box">
                <Lock size={18} color="#10b981" />
                <p>
                  <strong>Technical Verification:</strong> You can verify this by opening your 
                  browser's Developer Tools (F12) and checking the Network tab while using the 
                  calculator. You'll see that no data is sent to any server.
                </p>
              </div>
            </article>

            {/* Section 2: What Third Parties Collect */}
            <article className="privacy-section">
              <h3>2. What Third-Party Services Collect</h3>
              <p>
                While we don't collect your data directly, we use third-party services that 
                collect information to provide analytics and advertising:
              </p>

              {/* Google Analytics */}
              <div className="third-party-service">
                <h4>Google Analytics</h4>
                <p>
                  We use Google Analytics to understand how visitors use our site, which helps 
                  us improve the calculator and content.
                </p>
                <div className="data-collected">
                  <h5>What Google Analytics Collects:</h5>
                  <ul>
                    <li>Pages you visit and time spent on each page</li>
                    <li>How you arrived at our site (referral source)</li>
                    <li>Your approximate location (city/country level, based on IP address)</li>
                    <li>Browser type, device type, and screen resolution</li>
                    <li>Anonymous user ID (not linked to your identity)</li>
                  </ul>
                </div>
                <div className="data-not-collected">
                  <h5>What Google Analytics Does NOT See:</h5>
                  <ul>
                    <li>Your debt amounts or interest rates you enter</li>
                    <li>Your calculation results or payment schedules</li>
                    <li>Your IP address (we've enabled IP anonymization)</li>
                    <li>Your personal financial situation</li>
                  </ul>
                </div>
                <p className="opt-out-info">
                  <strong>How to Opt Out:</strong> Install the{' '}
                  <a 
                    href="https://tools.google.com/dlpage/gaoptout" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="external-link"
                  >
                    Google Analytics Opt-out Browser Add-on
                    <ExternalLink size={14} />
                  </a>
                </p>
                <p className="privacy-link">
                  <strong>Learn More:</strong>{' '}
                  <a 
                    href="https://policies.google.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="external-link"
                  >
                    Google Privacy Policy
                    <ExternalLink size={14} />
                  </a>
                </p>
              </div>

              {/* Google AdSense */}
              <div className="third-party-service">
                <h4>Google AdSense (Advertising)</h4>
                <p>
                  We display advertisements through Google AdSense to support this free tool. 
                  Google and its advertising partners use cookies and similar technologies to 
                  show you relevant ads based on your interests.
                </p>
                <div className="data-collected">
                  <h5>What AdSense Collects:</h5>
                  <ul>
                    <li>Websites you've visited previously</li>
                    <li>Your approximate location</li>
                    <li>Information about the device and browser you're using</li>
                    <li>The ads you've viewed or clicked</li>
                    <li>Demographic information (age range, gender, interests)</li>
                  </ul>
                </div>
                <p className="how-ads-work">
                  <strong>How Personalized Ads Work:</strong> Google uses this information to 
                  show you ads that might be more relevant to you. For example, if you've 
                  recently searched for financial planning services, you might see related ads. 
                  This doesn't mean Google knows your specific financial situation—they only 
                  know you visited a debt calculator website.
                </p>
                <p className="opt-out-info">
                  <strong>How to Control Ad Personalization:</strong>
                </p>
                <ul className="opt-out-list">
                  <li>
                    Visit{' '}
                    <a 
                      href="https://www.google.com/settings/ads" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="external-link"
                    >
                      Google Ad Settings
                      <ExternalLink size={14} />
                    </a>
                    {' '}to opt out of personalized advertising
                  </li>
                  <li>
                    Use the{' '}
                    <a 
                      href="http://www.aboutads.info/choices/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="external-link"
                    >
                      Digital Advertising Alliance opt-out tool
                      <ExternalLink size={14} />
                    </a>
                  </li>
                  <li>
                    Enable "Do Not Track" in your browser settings (though not all advertisers honor this)
                  </li>
                  <li>
                    Use private/incognito browsing mode for more privacy
                  </li>
                </ul>
                <p className="privacy-link">
                  <strong>Learn More:</strong>{' '}
                  <a 
                    href="https://policies.google.com/technologies/ads" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="external-link"
                  >
                    How Google Uses Advertising Data
                    <ExternalLink size={14} />
                  </a>
                </p>
              </div>
            </article>

            {/* Section 3: Cookies */}
            <article className="privacy-section">
              <h3>3. Cookies and Similar Technologies</h3>
              <p>
                Our website and third-party services use cookies and similar tracking technologies.
              </p>
              
              <div className="cookie-types">
                <div className="cookie-type">
                  <h4>
                    <Cookie size={20} color="#ff6b35" />
                    What Are Cookies?
                  </h4>
                  <p>
                    Cookies are small text files stored on your device that help websites remember 
                    information about your visit. They're used for analytics, advertising, and 
                    improving user experience.
                  </p>
                </div>

                <div className="cookie-category">
                  <h5>Essential Cookies (First-Party)</h5>
                  <p>
                    We may use essential cookies to remember your preferences (like theme selection). 
                    These cookies do not track you across websites.
                  </p>
                </div>

                <div className="cookie-category">
                  <h5>Analytics Cookies (Google Analytics)</h5>
                  <p>
                    These cookies help us understand how visitors interact with our website by 
                    collecting information anonymously.
                  </p>
                </div>

                <div className="cookie-category">
                  <h5>Advertising Cookies (Google AdSense)</h5>
                  <p>
                    These cookies are used to show you relevant advertisements. They may track 
                    your browsing across multiple websites.
                  </p>
                </div>
              </div>

              <div className="info-box">
                <h5>How to Manage Cookies:</h5>
                <ul>
                  <li>Most browsers allow you to refuse cookies through settings</li>
                  <li>You can delete existing cookies from your browser</li>
                  <li>Be aware that blocking cookies may affect website functionality</li>
                  <li>Use browser extensions like uBlock Origin or Privacy Badger for more control</li>
                </ul>
              </div>
            </article>

            {/* Section 4: How Information Is Used */}
            <article className="privacy-section">
              <h3>4. How Collected Information Is Used</h3>
              <p>
                The information collected by Google Analytics and AdSense is used for:
              </p>
              <ul className="privacy-list">
                <li>
                  <strong>Improving Our Service:</strong> Understanding which features are most 
                  used helps us enhance the calculator and add valuable content.
                </li>
                <li>
                  <strong>Site Performance:</strong> Identifying technical issues and optimizing 
                  loading times for better user experience.
                </li>
                <li>
                  <strong>Content Planning:</strong> Seeing which educational articles are most 
                  helpful guides our content strategy.
                </li>
                <li>
                  <strong>Advertising Revenue:</strong> Ads support this free tool. Personalized 
                  ads perform better, allowing us to continue providing free access.
                </li>
              </ul>
            </article>

            {/* Section 5: Data Sharing */}
            <article className="privacy-section">
              <h3>5. Data Sharing and Third Parties</h3>
              <p>
                We do not sell, trade, or transfer your information to third parties, because 
                we don't collect personal information in the first place. However:
              </p>
              <ul className="privacy-list">
                <li>
                  <strong>Google Analytics and AdSense:</strong> These services collect data 
                  directly from your browser. Google may share anonymized, aggregated data 
                  with their partners. See their privacy policies for details.
                </li>
                <li>
                  <strong>Legal Requirements:</strong> If required by law, we may need to 
                  disclose information to comply with legal processes.
                </li>
                <li>
                  <strong>No Marketing Lists:</strong> We don't build or sell marketing lists 
                  because we don't collect email addresses or contact information.
                </li>
              </ul>
            </article>

            {/* Section 6: Data Security */}
            <article className="privacy-section">
              <h3>6. Data Security</h3>
              <div className="security-measures">
                <div className="security-item">
                  <Lock size={24} color="#10b981" />
                  <div>
                    <h4>Client-Side Processing</h4>
                    <p>
                      Your financial calculations never leave your device, providing the highest 
                      level of security for your sensitive information.
                    </p>
                  </div>
                </div>
                <div className="security-item">
                  <Shield size={24} color="#3b82f6" />
                  <div>
                    <h4>HTTPS Encryption</h4>
                    <p>
                      Our website uses HTTPS encryption to protect data transmitted between 
                      your browser and our servers.
                    </p>
                  </div>
                </div>
                <div className="security-item">
                  <Eye size={24} color="#ff6b35" />
                  <div>
                    <h4>No Data Storage</h4>
                    <p>
                      Since we don't store your calculation data, there's no database that 
                      could be breached or compromised.
                    </p>
                  </div>
                </div>
              </div>
            </article>

            {/* Section 7: Your Rights */}
            <article className="privacy-section">
              <h3>7. Your Privacy Rights</h3>
              <p>
                Depending on your location, you may have certain rights regarding your data:
              </p>
              
              <div className="rights-section">
                <h4>GDPR (European Union)</h4>
                <p>If you're in the EU, you have the right to:</p>
                <ul>
                  <li>Access the personal data held about you</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your data (right to be forgotten)</li>
                  <li>Object to processing of your data</li>
                  <li>Data portability</li>
                </ul>
                <p className="note">
                  <strong>Note:</strong> Since we don't collect or store personal data directly, 
                  these rights primarily apply to data held by Google. Contact Google directly 
                  to exercise these rights regarding Analytics and AdSense data.
                </p>
              </div>

              <div className="rights-section">
                <h4>CCPA (California)</h4>
                <p>California residents have the right to:</p>
                <ul>
                  <li>Know what personal information is collected</li>
                  <li>Know if personal information is sold or disclosed</li>
                  <li>Opt out of the sale of personal information</li>
                  <li>Request deletion of personal information</li>
                </ul>
                <p className="note">
                  <strong>Our Compliance:</strong> We do not sell personal information. 
                  Third-party cookies from Google AdSense may be considered "sharing" under 
                  CCPA, which you can control through Google Ad Settings.
                </p>
              </div>
            </article>

            {/* Section 8: Children's Privacy */}
            <article className="privacy-section">
              <h3>8. Children's Privacy</h3>
              <p>
                Our service is not directed to individuals under the age of 18. We do not 
                knowingly collect personal information from children. If you're a parent or 
                guardian and believe your child has used our site, please contact us.
              </p>
              <p>
                Google's services also prohibit personalized advertising to children under 13 
                (or applicable age in your country).
              </p>
            </article>

            {/* Section 9: International Users */}
            <article className="privacy-section">
              <h3>9. International Data Transfers</h3>
              <p>
                Our website may be accessed from countries around the world. Google Analytics 
                and AdSense may transfer and process data in the United States and other 
                countries. By using our site, you consent to such transfers.
              </p>
              <p>
                Google complies with data protection frameworks for international data transfers. 
                Learn more at{' '}
                <a 
                  href="https://policies.google.com/privacy/frameworks" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="external-link"
                >
                  Google's Privacy Frameworks
                  <ExternalLink size={14} />
                </a>
              </p>
            </article>

            {/* Section 10: Changes to Privacy Policy */}
            <article className="privacy-section">
              <h3>10. Changes to This Privacy Policy</h3>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our 
                practices or legal requirements. We will notify users of significant changes by:
              </p>
              <ul className="privacy-list">
                <li>Updating the "Last Updated" date at the top of this section</li>
                <li>Posting a notice on our homepage for 30 days after major changes</li>
              </ul>
              <p>
                We encourage you to review this Privacy Policy periodically. Your continued use 
                of the website after changes constitutes acceptance of the updated policy.
              </p>
            </article>

            {/* Section 11: Contact Information */}
            <article className="privacy-section contact-section">
              <h3>11. Contact Us</h3>
              <div className="contact-box">
                <Mail size={24} color="#ff6b35" />
                <div>
                  <p>
                    If you have questions, concerns, or requests regarding this Privacy Policy 
                    or your data, please contact us:
                  </p>
                  <div className="contact-details">
                    <p><strong>Email:</strong> contact@zerodebtjourney.com</p>
                    <p><strong>Response Time:</strong> We aim to respond within 48 hours</p>
                  </div>
                  <p className="contact-note">
                    <strong>Important:</strong> We are not financial advisors and cannot provide 
                    personalized financial advice. For questions about the calculator functionality 
                    or privacy matters only.
                  </p>
                </div>
              </div>
            </article>

            {/* Additional Resources */}
            <article className="privacy-section resources-section">
              <h3>Additional Privacy Resources</h3>
              <p>Learn more about online privacy and how to protect yourself:</p>
              <ul className="resource-list">
                <li>
                  <a 
                    href="https://www.eff.org/issues/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="external-link"
                  >
                    Electronic Frontier Foundation - Privacy
                    <ExternalLink size={14} />
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.consumer.ftc.gov/topics/privacy-identity-online-security" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="external-link"
                  >
                    FTC - Privacy and Security
                    <ExternalLink size={14} />
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.privacytools.io/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="external-link"
                  >
                    PrivacyTools.io - Privacy Tools & Resources
                    <ExternalLink size={14} />
                  </a>
                </li>
              </ul>
            </article>

          </div>

          {/* Footer Note */}
          <div className="privacy-footer">
            <p>
              <strong>Last Updated:</strong> December 14, 2024
            </p>
            <p>
              This privacy policy is effective immediately for new users and will be effective 
              for existing users 30 days after posting.
            </p>
          </div>

        </div>

      </div>
    </section>
  )
}

export default PrivacyPolicy
